// MAP-FIRE render (owner 2026-11-02: 'explosions, fires, can char the ground or trees or start fires that grow or expand
// and burn the vegetation'; 'flames lick upward and flicker, smoke columns rise from burning trees, embers drift, the fire
// front is irregular; burnt grass stays charred'). Reads Sim.fire + the props only.
//  - cel flame cards (hard red / orange / amber / yellow bands, noise-licked edges that rise and flicker), two per burning
//    grass cell (taller and brighter on the fresh front), a crown of big flames on each burning tree
//  - dark smoke columns from burning trees and a little from the grass, embers drifting up
//  - the ground is charred where the fire passed (ground.stampBurn: lit cells, then again when they burn out)
// Instanced and capped; nothing is allocated per frame.
import * as THREE from 'three/webgpu';
import { abs, color, cos, float, instancedBufferAttribute, max, mix, mx_noise_float, pow, sin, smoothstep, time, uv, vec3 } from 'three/tsl';
import type { Sim } from '../core/sim';
import { CELL } from '../core/fire';
import type { Fx } from './fx';
import type { GroundDeform } from './ground';
import { TREE_K } from './props';
import type { FloraShape } from './flora';
import type { Prop } from '../core/terrain';
/** the drawn tree / bush shapes + transforms (set by the view): crown flames follow each variant's lobes, lean and scale */
export interface FloraSource { trees: FloraShape[]; bushes: FloraShape[]; transformOf(p: Prop, out: THREE.Matrix4): THREE.Matrix4 }
const _mw = new THREE.Matrix4(), _v = new THREE.Vector3(), _sv = new THREE.Vector3();

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
/** grass-fire flame cards (owner 2026-11-02: 'flames on the grass is way too big, make them much much smaller'): size scale
 *  (was 1 → about 0.5-1 m tall) and a few more, smaller licks per 2 m cell so the burning line still reads */
const GRASS_FLAME_K = 0.3, GRASS_FLAMES = 4;
/** grass flames: seconds at full size after a patch catches, then seconds to shrink away (owner 2026-11-02) */
const GRASS_FLAME_HOLD = 1, GRASS_FLAME_FADE = 1.2;
/** canopy flame anchors on the painted tree (local units, before the tree scale) */
const CROWN: [number, number, number, number][] = [[0, 3.6, 0, 1.4], [0.85, 3.0, 0.3, 1.05], [-0.85, 3.05, -0.25, 1.05], [0.15, 2.9, -0.85, 1.0], [-0.1, 3.0, 0.85, 1.0], [0.2, 4.2, 0.1, 1.0], [0, 0.4, 0.12, 0.6]];
const _f = new THREE.Vector3();

export class FireFx {
  private mesh: THREE.InstancedMesh;
  private fl: THREE.InstancedBufferAttribute;
  private cap: number;
  private marked: Uint8Array | null = null;
  private prev: number[] = []; private cur: number[] = [];
  private smokeT = 0; private emberT = 0;
  private t = 0;
  flora: FloraSource | null = null;

  constructor(scene: THREE.Scene, quality: string, private ground: GroundDeform) {
    this.cap = quality === 'low' ? 380 : 760;
    this.fl = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 2), 2); this.fl.setUsage(THREE.DynamicDrawUsage);
    const g = new THREE.PlaneGeometry(1, 1, 1, 1).translate(0, 0.5, 0); // pivot at the base
    const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, alphaTest: 0.5, fog: false });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const F = instancedBufferAttribute(this.fl) as any;
    const seed = F.x, heat = F.y;
    const u = uv(), x = u.x.sub(0.5).mul(2), y = u.y;
    // tongue shape: widest low down, tapering to a tip; noise rising through it licks the edge and splits the tip
    const w0 = pow(float(1).sub(y), 0.7).mul(smoothstep(0.0, 0.18, y).mul(0.4).add(0.6)).mul(0.95);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const n = (mx_noise_float(vec3(x.mul(1.7), y.mul(2.4).sub(time.mul(2.6)), seed.mul(17))) as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const n2 = (mx_noise_float(vec3(x.mul(3.6), y.mul(5).sub(time.mul(4.2)), seed.mul(5).add(3))) as any);
    const sway = sin(y.mul(3.2).add(time.mul(5.5)).add(seed.mul(20))).mul(0.14).mul(y);
    // several tongues: the upper half splits into 2–3 licking tips that wander with the noise
    const tongues = cos(x.mul(Math.PI * 1.7).add(seed.mul(6)).add(n.mul(2.2)).add(time.mul(1.3))).mul(0.5).add(0.5);
    const w = w0.mul(mix(float(1), tongues.mul(0.95).add(0.05), smoothstep(0.12, 0.6, y)));
    const shape = w.add(n.mul(0.3).mul(y.add(0.2))).add(n2.mul(0.1)).sub(abs(x.add(sway)).mul(0.85));
    const k = shape.div(max(w0, 0.08)).add(float(1).sub(y).mul(0.25)).add(heat.mul(0.1)); // 0 at the edge → 1+ in the hot core
    let col = mix(color(0x4a0804), color(0xd8300c), smoothstep(0.06, 0.1, k)); // thin dark-red ink edge (cel)
    col = mix(col, color(0xff6a14), smoothstep(0.3, 0.34, k));
    col = mix(col, color(0xffa526), smoothstep(0.52, 0.56, k));
    col = mix(col, color(0xffe07a), smoothstep(0.78, 0.82, k).mul(smoothstep(0.8, 0.5, y)));
    m.colorNode = col.mul(heat.mul(0.3).add(0.8));
    m.opacityNode = smoothstep(-0.01, 0.01, shape);
    this.mesh = new THREE.InstancedMesh(g, m, this.cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  clear(): void { this.mesh.count = 0; this.marked = null; this.prev.length = 0; this.cur.length = 0; }

  private hash(j: number): number { const h = Math.sin(j * 91.345 + 7.13) * 43758.5453; return h - Math.floor(h); }

  update(sim: Sim, dt: number, camQ: THREE.Quaternion, fx: Fx): void {
    this.t += dt;
    const F = sim.fire, cs = 2;
    if (!this.marked || this.marked.length !== F.state.length) { this.marked = new Uint8Array(F.state.length); this.prev.length = 0; }
    const mk = this.marked, fl = this.fl.array as Float32Array;
    let n = 0;
    // grass fire: char the ground once when a cell catches, again when it burns out
    const cur = this.cur; cur.length = 0;
    for (const j of F.burning) {
      cur.push(j);
      const x = F.cx(j), z = F.cz(j);
      if (!mk[j]) { mk[j] = 1; this.ground.stampBurn(x + (this.hash(j) - 0.5), z + (this.hash(j + 7) - 0.5), 1.7); }
      const age = F.t[j], life = F.life[j], u = age / life;
      // owner 2026-11-02: 'make the flames on the grass last for a 1 sec and slowly disappear' — each patch flares up fast,
      // burns for GRASS_FLAME_HOLD s, then shrinks away over GRASS_FLAME_FADE s (the cell keeps smouldering/spreading in the sim,
      // so the visible flames ride the advancing front and leave char behind)
      const fadeU = Math.min(1, Math.max(0, (age - GRASS_FLAME_HOLD) / GRASS_FLAME_FADE)), fade = 1 - fadeU * fadeU * (3 - 2 * fadeU);
      const grow = Math.min(1, age / 0.15), die = 1 - Math.max(0, (u - 0.62) / 0.38), front = age < 1.4 ? 1 + (1.4 - age) * 0.35 : 1;
      const hgt = grow * die * front * fade;
      if (hgt <= 0.02) continue;
      for (let f = 0; f < GRASS_FLAMES && n < this.cap; f++) {
        const h1 = this.hash(j * 3 + f), h2 = this.hash(j * 3 + f + 101);
        const fx0 = x + (h1 - 0.5) * cs, fz0 = z + (h2 - 0.5) * cs;
        const flick = 1 + Math.sin(this.t * (9 + h1 * 5) + h2 * 20) * 0.12;
        const H = (1.5 + h1 * 1.7) * hgt * flick * GRASS_FLAME_K, Wd = (1.7 + h2 * 1.1) * Math.min(1, 0.5 + hgt * 0.5) * GRASS_FLAME_K;
        _p.set(fx0, this.ground.sample(fx0, fz0) - 0.05, fz0); _s.set(Wd, H, Wd);
        this.mesh.setMatrixAt(n, _m.compose(_p, camQ, _s));
        fl[n * 2] = h1 * 7 + f; fl[n * 2 + 1] = front > 1 ? 1 : 0.5 * die;
        n++;
      }
    }
    for (const j of this.prev) if (F.state[j] !== CELL.burning && mk[j] === 1) { mk[j] = 2; this.ground.stampBurn(F.cx(j), F.cz(j), 1.4); }
    for (let k = 0; k < this.prev.length; k++) { const j = this.prev[k]; if (F.state[j] === CELL.fresh) mk[j] = 0; }
    this.prev.length = 0; for (const j of cur) this.prev.push(j);
    // burning trees: a crown of big flames, smoke columns, embers
    this.smokeT += dt; this.emberT += dt;
    const smokeNow = this.smokeT > 0.16, emberNow = this.emberT > 0.12;
    if (smokeNow) this.smokeT = 0; if (emberNow) this.emberT = 0;
    const blast = fx.blast, smokeOk = !blast || blast.smokeLoad < 0.85;
    _f.set(0, 0, 1).applyQuaternion(camQ); // toward the camera: crown flames sit on the canopy's near side so they read
    for (const p of sim.terrain.props) {
      if (!p.alive || p.burn <= 0) continue;
      // variant crowns (owner 2026-11-02 tree / bush variants): anchors go through the drawn transform (scale, lean, yaw)
      const bush = p.kind === 'bush', F = this.flora, shp = F ? (bush ? F.bushes : F.trees)[p.v] ?? null : null;
      const crown = shp?.crown ?? CROWN;
      let S = p.s * TREE_K; if (F) { F.transformOf(p, _mw); S = _sv.setFromMatrixScale(_mw).y; }
      const gy = this.ground.sample(p.x, p.z), grow = Math.min(1, p.burn / (bush ? 0.6 : 1.5)), c = Math.cos(p.rot), s = Math.sin(p.rot), fk = bush ? 2.1 : 1.35;
      for (let k = 0; k < crown.length && n < this.cap; k++) {
        const [lx, ly, lz, sz] = crown[k], h1 = this.hash(k * 13 + Math.floor(p.x * 7));
        let wx = p.x + (lx * c + lz * s) * S, wy = gy + ly * S, wz = p.z + (-lx * s + lz * c) * S;
        if (F) { _v.set(lx, ly, lz).applyMatrix4(_mw); wx = _v.x; wy = _v.y; wz = _v.z; }
        const flick = 1 + Math.sin(this.t * (8 + h1 * 6) + k * 2.1 + p.z) * 0.14;
        const trunk = !bush && k === crown.length - 1, H = sz * S * fk * grow * flick * (trunk ? Math.min(1, p.burn / 3) : 1), out = trunk ? 0.3 * S : (bush ? 0.5 : 1.0) * S;
        _p.set(wx + _f.x * out, wy - H * 0.3 + _f.y * out, wz + _f.z * out); _s.set(H * 0.85, H, H * 0.85);
        this.mesh.setMatrixAt(n, _m.compose(_p, camQ, _s));
        fl[n * 2] = h1 * 9 + k; fl[n * 2 + 1] = 0.9;
        n++;
      }
      const top = gy + (shp?.top ?? 3.6) * S, cr = (shp?.canopyR ?? 1) * S, ey = (shp?.canopyY ?? 2.9) * S, sk = bush ? 0.5 : 1;
      if (smokeNow && smokeOk && blast) blast.dust(p.x + (Math.random() - 0.5) * cr, top, p.z + (Math.random() - 0.5) * cr, 0.5 + Math.random() * 0.4, 2.2 + Math.random() * 1.2, 0.2, 3 + Math.random() * 1.8, (1 + 0.4 * S) * sk, (3.4 + S * 1.1) * sk, 1.9);
      if (emberNow) fx.particles.emitDir(p.x + (Math.random() - 0.5) * 1.6 * cr, gy + ey + (Math.random() - 0.3) * cr, p.z + (Math.random() - 0.5) * 1.6 * cr, (Math.random() - 0.3) * 1.2, 2 + Math.random() * 2.5, (Math.random() - 0.5) * 1.2, Math.random() < 0.5 ? 0xff7a20 : 0xffc040, 0.12 + Math.random() * 0.1, 1.4 + Math.random(), 4);
    }
    // a little smoke and a few embers off the grass fire
    const B = F.burning;
    if (B.length) {
      if (smokeNow && smokeOk && blast) for (let k = 0; k < Math.min(2, 1 + (B.length >> 5)); k++) {
        const j = B[(Math.random() * B.length) | 0], x = F.cx(j), z = F.cz(j);
        blast.dust(x, this.ground.sample(x, z) + 0.8, z, 0.4, 1.4 + Math.random(), 0.15, 2 + Math.random() * 1.2, 0.6, 2 + Math.random(), 1.7);
      }
      if (emberNow) for (let k = 0; k < Math.min(3, 1 + (B.length >> 4)); k++) {
        const j = B[(Math.random() * B.length) | 0], x = F.cx(j) + (Math.random() - 0.5) * 2, z = F.cz(j) + (Math.random() - 0.5) * 2;
        fx.particles.emitDir(x, this.ground.sample(x, z) + 0.6, z, (Math.random() - 0.5), 1.5 + Math.random() * 2, (Math.random() - 0.5), 0xff8a2a, 0.1 + Math.random() * 0.08, 1 + Math.random() * 0.8, 4);
      }
    }
    this.mesh.count = n; this.mesh.instanceMatrix.needsUpdate = true; this.fl.needsUpdate = true;
  }
}
